import React, { useMemo } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import type { EventPageType } from '@graphql/generated-base';
import { BrannanNavigation } from '@graphql/generated-base';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { Box } from '@withjoy/joykit';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { isHeaderLayoutWithJsonContainerId } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { isHeaderLayoutWithPhotos } from '@shared/utils/websiteDesign';
import { useTranslation } from '@shared/core';
import { useLayout } from '@shared/context/LayoutProvider';
import { useFetchWebsitePageListData } from '@shared/hooks/useWebsitePages';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useEventInfo } from '@shared/utils/eventInfo';

// The page-width variants (photoSliderPageWidth, photoMarqueePageWidth) are intentionally absent —
// they render within the page content column, unlike photoSlider/photoMarquee which break out to
// full viewport width.
const FULL_WIDTH_LAYOUTS: PhotoPresentationLayout[] = ['fullWidth', 'photoSlider', 'photoTrio', 'photoDuoLeft', 'photoDuoRight', 'photoMarquee', 'videoArch'];

export interface HeaderPhotoV2Props {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageSlug?: string;
  pageType: Maybe<EventPageType>;
  showPlaceholder?: boolean;
  children: React.ReactNode;
}

export const HeaderPhoto = (props: HeaderPhotoV2Props) => {
  const { t } = useTranslation('joykit');
  const tInlineEditor = t('inlineEditor');
  const { children, websiteHeader, pageSlug, pageType, showPlaceholder = false } = props;
  const { brannanNavigation } = useLayout();

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  };

  const handleEditMonogramClick = () => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editMonogram' },
    });
  };

  const isMonogram = websiteHeader.headerPhoto?.presentationLayout === 'monogram';
  const isVideoArch = websiteHeader.headerPhoto?.presentationLayout === 'videoArch';

  const elementLabel = useMemo(() => {
    if (isMonogram) {
      return tInlineEditor.type.monogram();
    }
    return tInlineEditor.type.photo();
  }, [isMonogram, tInlineEditor.type]);

  const hasContent = useMemo(() => {
    if (isHeaderLayoutWithPhotos(websiteHeader.headerPhoto)) {
      return websiteHeader.headerPhoto.photos.length > 0;
    }

    if (isHeaderLayoutWithJsonContainerId(websiteHeader.headerPhoto)) {
      return !!websiteHeader.headerPhoto.jsonContainerId;
    }

    return false;
  }, [websiteHeader.headerPhoto]);
  const { eventHandle } = useEventInfo();
  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();
  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();
  // pageIndex only needs the lightweight WebsitePageList query (SSR-prefetched, and the exact
  // source useEventWebsiteGuestData merges `pages` from). Waiting on the heavy EventWebsiteGuest
  // query kept the header unmounted past first paint on Brannan classic, shifting content down
  // when it finally rendered. forceAdmin mirrors useEventWebsiteGuestData's isAdminDashboard arg.
  // ?preview=true (how joy's own preview iframe identifies itself) instead of isInIframe():
  // the query param is visible to SSR too, so forceAdmin can't flip at hydration and
  // invalidate the SSR-cached page list.
  const isAdminPreview = useQueryParamHelper().getValueString('preview') === 'true';
  const { data: pageListData, loadingPages } = useFetchWebsitePageListData(eventHandle || '', {
    forceAdmin: isAdmin && isAdminPreview,
    // Mirror useEventWebsiteGuestData's auth guard: on non-SSR guest sites, wait for guest
    // identification so the query carries the token — else Apollo caches a restricted page list.
    skip: !isGuestSiteSsrEnabled && !hasIdentifiedUserOnce,
  });
  const pages = pageListData?.eventByName?.pages;
  // `loadingPages && !pageListData` (not raw loadingPages): background refetches must not flip
  // the gate to loading and unmount the header — the exact shift this change prevents.
  const loading = loadingPages && !pageListData;
  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  const hasTopPadding = useMemo(() => {
    if (isVideoArch) {
      // For videoArch the padding is handled by the VideoArch component
      return false;
    }
    return hasContent || showPlaceholder;
  }, [hasContent, showPlaceholder, isVideoArch]);

  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage } = useMemo(() => checkIsWelcomePage(pageSlug, pageType), [checkIsWelcomePage, pageSlug, pageType]);

  const isPhotoFullWidth = FULL_WIDTH_LAYOUTS.includes(websiteHeader.headerPhoto?.presentationLayout);

  // videoArch needs height: 100% on the outer Box, InlineEditor wrapper, and InlineEditor container
  // so VideoArchLayout's 100dvh flex parent passes height down through every nested element to the video.
  const fullWidthWrapperCSS = useMemo(() => {
    if (!isPhotoFullWidth) return {};
    const base = { width: '100vw', left: 'calc(-50vw + 50%)' };
    return isVideoArch ? { ...base, height: '100%' } : base;
  }, [isPhotoFullWidth, isVideoArch]);

  if (isWelcomePage || (!loading && pageIndex === 0) || brannanNavigation === BrannanNavigation.singlePage) {
    return (
      <Box
        paddingTop={hasTopPadding ? { _: 0, sm4: 9 } : 0}
        height={isVideoArch ? '100%' : undefined}
        {...(isPhotoFullWidth && { 'data-photo-width': 'full' })}
      >
        <InlineEditor
          elementLabel={elementLabel}
          wrapperType="actionInside"
          actionData={
            isMonogram
              ? {
                  editMonogram: handleEditMonogramClick,
                }
              : {
                  editPhoto: handleEditPhotoClick,
                }
          }
          componentName="headerPhoto"
          pageName={pageType}
          pageSlug={pageSlug || ''}
          isEligibleForInlineEditing={!isVideoArch}
          wrapperCSS={fullWidthWrapperCSS}
          containerCSS={isVideoArch ? { height: '100%' } : undefined}
        >
          {children}
        </InlineEditor>
      </Box>
    );
  }

  return null;
};
